import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { tokenStorage } from '../lib/tokenStorage';

// API Configuration
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'https://api.cricclash.com') + '/api/v1';

// Response interfaces
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    userId?: string;
    email: string;
    firstName: string;
    lastName: string;
    firstname?: string;
    lastname?: string;
    role: string;
    avatar?: string;
    image?: string;
  };
}

// Actual API response structure
interface LoginApiResponse {
  AccessToken: string;
  RefreshToken: string;
  IdToken: string;
  TokenType: string;
  ExpiresIn: number;
}

interface User {
  id: string;
  userId?: string;
  email: string;
  firstName: string;
  lastName: string;
  firstname?: string;
  lastname?: string;
  role: string;
  avatar?: string;
  image?: string;
  username?: string;
  phonenumber?: string;
  isVerified?: boolean;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Series {
  id: string;
  seriesId: number;
  name: string;
  alternateName?: string;
  shortName?: string;
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'ongoing' | 'in_progress' | 'completed';
  teams: string[];
  format: string;
  type?: 'international' | 'league' | 'women' | 'domestic';
  description?: string;
  publish?: boolean;
  published?: boolean;
  active?: boolean;
  trending?: boolean;
  isLiveStreamEnabled?: boolean;
  matchCount?: number;
  teamCount?: number;
  image?: string;
  imageUploaded?: boolean;
}

interface Team {
  id: string;
  name: string;
  shortName: string;
  logo?: string;
  country: string;
  players?: Player[];
}

interface Player {
  id: string;
  name: string;
  role: 'batsman' | 'bowler' | 'all-rounder' | 'wicket-keeper';
  teamId: string;
  avatar?: string;
  credits?: number;
  points?: number;
}

interface Match {
  id: string;
  title: string;
  team1: string;
  team2: string;
  format: string;
  venue: string;
  date: string;
  time: string;
  status: 'upcoming' | 'live' | 'completed';
  result?: string;
  series: string;
  seriesId: string;
}

interface Contest {
  id: string;
  matchId: string;
  name: string;
  entryFee: number;
  maxTeams: number;
  prizePool: number;
  type: 'public' | 'private';
  status: 'upcoming' | 'live' | 'completed';
  totalSpots: number;
  filledSpots: number;
}

interface FantasyTeam {
  id: string;
  userId: string;
  matchId: string;
  contestId?: string;
  name: string;
  captain: string;
  viceCaptain: string;
  players: string[];
  totalPoints: number;
  rank?: number;
}

class ApiService {
  private api: AxiosInstance;
  private refreshTokenFailureCount: number = 0;
  private readonly MAX_REFRESH_FAILURES = 5;
  private onTokenRefreshed?: (token: string) => void;
  private onAuthFailed?: () => void;
  private isRefreshing: boolean = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  /** Register callbacks so Zustand store stays in sync */
  setAuthCallbacks(onTokenRefreshed: (token: string) => void, onAuthFailed: () => void) {
    this.onTokenRefreshed = onTokenRefreshed;
    this.onAuthFailed = onAuthFailed;
  }

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Initialize failure count
    this.refreshTokenFailureCount = tokenStorage.getRefreshFailCount();

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        // Skip overwriting if header was already set (e.g. retry after refresh)
        if (!config.headers.Authorization) {
          const token = tokenStorage.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling with improved token refresh logic
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle 401 errors with token refresh (queued to prevent concurrent refreshes)
        // Skip refresh for the refresh-token endpoint itself to avoid loops
        const isRefreshRequest = originalRequest.url?.includes('/auth/refresh-token');
        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
          originalRequest._retry = true;

          const currentRefreshToken = tokenStorage.getRefreshToken();
          if (!currentRefreshToken) {
            console.warn('⚠️ 401 - No refresh token available');
            this.logout();
            window.location.href = '/';
            return Promise.reject(error);
          }

          // If already refreshing, queue this request to retry after refresh completes
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.refreshSubscribers.push((newToken: string) => {
                if (!newToken) {
                  reject(new Error('Session expired. Please login again.'));
                  return;
                }
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                resolve(this.api(originalRequest));
              });
            });
          }

          // Start refresh
          this.isRefreshing = true;

          try {
            const response = await this.refreshToken(currentRefreshToken);
            const newToken = response.access_token;

            tokenStorage.setAccessToken(newToken);
            tokenStorage.setRefreshToken(response.refresh_token);
            this.resetRefreshFailureCount();
            this.onTokenRefreshed?.(newToken);

            console.log('✅ Token refreshed, retrying requests...');

            // Resolve all queued requests with the new token
            this.refreshSubscribers.forEach((cb) => cb(newToken));
            this.refreshSubscribers = [];

            // Retry the original request
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            console.error('❌ Token refresh failed:', refreshError);
            this.incrementRefreshFailureCount();

            // Reject all queued requests so they don't hang
            this.refreshSubscribers.forEach((cb) => cb(''));
            this.refreshSubscribers = [];

            const isServerError = refreshError instanceof AxiosError &&
              ((refreshError.response?.status && refreshError.response.status >= 500) ||
               refreshError.message.includes('Network Error'));

            if (isServerError) {
              return Promise.reject(new Error('Server is temporarily unavailable. Please try again.'));
            }

            this.logout();
            window.location.href = '/';
            return Promise.reject(new Error('Session expired. Please login again.'));
          } finally {
            this.isRefreshing = false;
          }
        }

        // If the refresh-token endpoint itself returns 401, logout immediately
        if (error.response?.status === 401 && isRefreshRequest) {
          console.warn('⚠️ Refresh token expired');
          this.logout();
          window.location.href = '/';
          return Promise.reject(new Error('Session expired. Please login again.'));
        }

        // Handle server errors (5xx)
        if (error.response?.status && error.response.status >= 500) {
          let errorMessage = "Server is temporarily unavailable. Please try again later.";
          if (error.response.status === 502) {
            errorMessage = "Server gateway error. Please try again in a moment.";
          } else if (error.response.status === 503) {
            errorMessage = "Service temporarily unavailable. Please try again.";
          } else if (error.response.status === 504) {
            errorMessage = "Server timeout. Please try again.";
          }
          return Promise.reject(new Error(errorMessage));
        }

        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<ApiResponse<LoginApiResponse>> = await this.api.post('/auth/login', credentials);

      if (response.data.success) {
        const { AccessToken, RefreshToken, IdToken } = response.data.data;

        // Decode user info from ID token (JWT)
        let userInfo: any = {};
        try {
          // Decode JWT token to get user info (simple base64 decode of payload)
          const payload = IdToken.split('.')[1];
          const decodedPayload = JSON.parse(atob(payload));

          userInfo = {
            id: decodedPayload.sub || decodedPayload['cognito:username'],
            email: decodedPayload.email,
            firstName: decodedPayload.given_name || 'User',
            lastName: decodedPayload.family_name || '',
            role: decodedPayload['cognito:groups']?.[0] || 'user',
            avatar: '',
            username: decodedPayload['cognito:username']
          };
        } catch (decodeError) {
          console.warn('Failed to decode ID token, using default user info:', decodeError);
          userInfo = {
            id: 'unknown',
            email: credentials.email,
            firstName: 'User',
            lastName: '',
            role: 'user',
            avatar: ''
          };
        }

        // Store tokens
        tokenStorage.setTokens(AccessToken, RefreshToken, IdToken);
        tokenStorage.setUser(userInfo);

        // Reset refresh failure count on successful login
        this.resetRefreshFailureCount();

        return {
          access_token: AccessToken,
          refresh_token: RefreshToken,
          user: userInfo
        };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const data = error.response?.data;
        const err: any = new Error(data?.message || 'Login failed');
        // Pass through error code and email for unconfirmed user handling
        if (data?.errors?.[0]?.code === 'USER_NOT_CONFIRMED' || data?.errors?.code === 'USER_NOT_CONFIRMED') {
          err.code = 'USER_NOT_CONFIRMED';
          err.email = data.errors?.[0]?.email || data.errors?.email;
        }
        throw err;
      }
      throw error;
    }
  }

  async refreshToken(refreshToken: string, email?: string): Promise<{ access_token: string; refresh_token: string }> {
    try {
      // Get email from stored user if not provided
      const userEmail = email || this.getCurrentUser()?.email;

      const idToken = tokenStorage.getIdToken();

      const requestData: any = { refreshToken };
      if (userEmail) {
        requestData.email = userEmail;
      }
      if (idToken) {
        requestData.idToken = idToken;
      }

      const response: AxiosResponse<any> =
        await this.api.post('/auth/refresh-token', requestData);

      if (response.data.success) {
        return {
          access_token: response.data.data.AccessToken,
          refresh_token: response.data.data.RefreshToken
        };
      } else {
        throw new Error(response.data.message || 'Token refresh failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Token refresh failed');
      }
      throw error;
    }
  }

  logout(): void {
    tokenStorage.clearAll();
    this.refreshTokenFailureCount = 0;
    // Sync Zustand store
    this.onAuthFailed?.();
  }

  private incrementRefreshFailureCount(): void {
    this.refreshTokenFailureCount++;
    tokenStorage.setRefreshFailCount(this.refreshTokenFailureCount);
    console.warn(`⚠️ Refresh failure count: ${this.refreshTokenFailureCount}/${this.MAX_REFRESH_FAILURES}`);
  }

  private resetRefreshFailureCount(): void {
    this.refreshTokenFailureCount = 0;
    tokenStorage.removeRefreshFailCount();
  }

  // Public authentication methods (user registration flow)
  async register(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<any> {
    try {
      // Transform to match Flutter's structure exactly
      const requestData = {
        firstname: data.firstName,
        lastname: data.lastName,
        email: data.email,
        password: data.password,
        dob: '1994-07-10', // Default DOB like Flutter
        gender: 'male', // Default gender like Flutter
        phonenumber: '+12223334444' // Default phone like Flutter
      };

      const response: AxiosResponse<any> = await this.api.post('/auth/register', requestData);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Registration failed');
      }
      throw error;
    }
  }

  async verifyEmail(data: { email: string; code: string }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/verify-email', data);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Email verification failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Email verification failed');
      }
      throw error;
    }
  }

  async resendVerifyEmail(email: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/resend-verify-email', { email });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to resend verification email');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to resend verification email');
      }
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/forgot-password', { email });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to send password reset email');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to send password reset email');
      }
      throw error;
    }
  }

  async resetPassword(data: { email: string; code: string; password: string }): Promise<any> {
    try {
      // Match Flutter's field names
      const response: AxiosResponse<any> = await this.api.post('/auth/reset-password', {
        email: data.email,
        newPassword: data.password,
        otp: data.code
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Password reset failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Password reset failed');
      }
      throw error;
    }
  }

  async userExists(email: string): Promise<boolean> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/auth/checkUserExist', { email });
      return response.data.success || false;
    } catch (error) {
      return false;
    }
  }

  // User profile methods
  async getUserProfile(): Promise<User> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/user/profile');

      if (response.data.success) {
        // Map API response to frontend User interface
        const apiUser = response.data.data;
        const mappedUser: User = {
          id: apiUser.userId || apiUser.id,
          email: apiUser.email,
          firstName: apiUser.firstname || apiUser.firstName,
          lastName: apiUser.lastname || apiUser.lastName,
          role: apiUser.role,
          avatar: apiUser.image || apiUser.avatar,
          username: apiUser.email,
          phonenumber: apiUser.phonenumber,
          isVerified: apiUser.isVerified,
          active: apiUser.active,
          createdAt: apiUser.createdAt,
          updatedAt: apiUser.updatedAt
        };

        tokenStorage.setUser(mappedUser);
        return mappedUser;
      } else {
        throw new Error(response.data.message || 'Failed to fetch user profile');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch user profile');
      }
      throw error;
    }
  }

  async updateUserProfile(userData: Partial<User>): Promise<User> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.put('/user/profile', userData);

      if (response.data.success) {
        tokenStorage.setUser(response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update user profile');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update user profile');
      }
      throw error;
    }
  }

  // Normalize MongoDB _id to id
  private normalizeId<T extends Record<string, any>>(item: T): T & { id: string } {
    return { ...item, id: item.id || item._id };
  }

  private normalizeIds<T extends Record<string, any>>(items: T[]): (T & { id: string })[] {
    return items.map((item) => this.normalizeId(item));
  }

  // Maintenance check (public, no auth required)
  async checkMaintenance(): Promise<{ isEnabled: boolean; message?: string; estimatedEndTime?: string }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/app-settings/maintenance/check`);
      if (response.data.success) {
        return response.data.data;
      }
      return { isEnabled: false };
    } catch {
      return { isEnabled: false };
    }
  }

  // Series methods (public)
  async getSeries(): Promise<Series[]> {
    try {
      const response: AxiosResponse<ApiResponse<Series[]>> = await this.api.get('/series');

      if (response.data.success) {
        return this.normalizeIds(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch series');
      }
      throw error;
    }
  }

  async getSeriesById(seriesId: string): Promise<Series> {
    try {
      const response: AxiosResponse<ApiResponse<Series>> = await this.api.get(`/series/${seriesId}`);

      if (response.data.success) {
        return this.normalizeId(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch series');
      }
      throw error;
    }
  }

  // Match methods (public)
  async getMatches(seriesId: string): Promise<Match[]> {
    try {
      const response: AxiosResponse<ApiResponse<Match[]>> = await this.api.get(`/series/${seriesId}/matches`);

      if (response.data.success) {
        return this.normalizeIds(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch matches');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch matches');
      }
      throw error;
    }
  }

  async getMatchById(seriesId: string, matchId: string): Promise<Match> {
    try {
      const response: AxiosResponse<ApiResponse<Match>> = await this.api.get(`/series/${seriesId}/match/${matchId}`);

      if (response.data.success) {
        return this.normalizeId(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch match');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch match');
      }
      throw error;
    }
  }

  // Contest methods (user)
  async getContests(): Promise<Contest[]> {
    try {
      const response: AxiosResponse<ApiResponse<Contest[]>> = await this.api.get('/contest');

      if (response.data.success) {
        return this.normalizeIds(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch contests');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contests');
      }
      throw error;
    }
  }

  async joinContest(contestId: string, code?: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/contest-member', { contestId, code });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to join contest');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to join contest');
      }
      throw error;
    }
  }

  async joinContestByCode(code: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/contest-member', { code });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to join contest');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to join contest');
      }
      throw error;
    }
  }

  async getContestLeaderboard(contestId: string, matchId?: number): Promise<any> {
    try {
      const params = matchId ? { matchId } : {};
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/contest/${contestId}/leaderboard`, { params });

      if (response.data.success) {
        const raw = response.data.data;
        // API returns { leaderboard: [...], lastProcessedMatch, ... } — extract and normalize
        const entries = Array.isArray(raw) ? raw : (raw?.leaderboard || []);
        const normalized = entries.map((e: any, i: number) => {
          const owner = e.ownerInfo || {};
          const team = e.fantasyTeam || {};
          return {
            ...e,
            rank: e.rank ?? i + 1,
            teamName: team.fantasyName || team.name || e.teamName || e.name || `Team ${i + 1}`,
            teamId: team.id || e.fantasyTeamId,
            ownerName: owner.firstname
              ? `${owner.firstname.charAt(0).toUpperCase()}${owner.firstname.slice(1)} ${(owner.lastname || '').charAt(0).toUpperCase()}${(owner.lastname || '').slice(1)}`.trim()
              : e.ownerName || e.owner || '',
            totalPoints: e.effectiveTotalPoints ?? e.bestOfTotalPoints ?? e.totalPoints ?? 0,
            points: e.totalPoints ?? 0,
          };
        });
        // Return full object with metadata when available
        if (!Array.isArray(raw) && raw?.leaderboard) {
          return {
            entries: normalized,
            lastProcessedMatch: raw.lastProcessedMatch || null,
            lastScoresUpdated: raw.lastScoresUpdated || raw.lastModified || null,
            lastLiveScoresUpdated: raw.lastLiveScoresUpdated || null,
            matchesScored: raw.matchesScored || 0,
            liveScoring: raw.liveScoring || false,
            liveMatchIds: raw.liveMatchIds || [],
          };
        }
        return { entries: normalized, lastProcessedMatch: null, lastScoresUpdated: null, matchesScored: 0 };
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest leaderboard');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest leaderboard');
      }
      throw error;
    }
  }

  // Fantasy Team methods — /api/v1/fantasy/*

  async createTeam(teamData: any): Promise<FantasyTeam> {
    try {
      const response: AxiosResponse<ApiResponse<FantasyTeam>> = await this.api.post('/fantasy/team', teamData);
      if (response.data.success) return this.normalizeId(response.data.data);
      throw new Error(response.data.message || 'Failed to create team');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to create team');
      throw error;
    }
  }

  async updateTeam(teamId: string, teamData: any): Promise<FantasyTeam> {
    try {
      const response: AxiosResponse<ApiResponse<FantasyTeam>> = await this.api.put(`/fantasy/team/${teamId}`, teamData);
      if (response.data.success) return this.normalizeId(response.data.data);
      throw new Error(response.data.message || 'Failed to update team');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to update team');
      throw error;
    }
  }

  async getTeamById(contestId: string, teamId: string): Promise<FantasyTeam> {
    try {
      const response: AxiosResponse<ApiResponse<FantasyTeam>> = await this.api.get(`/fantasy/${contestId}/team/${teamId}`);
      if (response.data.success) return this.normalizeId(response.data.data);
      throw new Error(response.data.message || 'Failed to fetch team');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch team');
      throw error;
    }
  }

  async deleteTeam(contestId: string, teamId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/fantasy/${contestId}/team/${teamId}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete team');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to delete team');
      throw error;
    }
  }

  async getContestTeams(contestId: string, matchId?: number): Promise<FantasyTeam[]> {
    try {
      const params = matchId ? { matchId } : {};
      const response: AxiosResponse<ApiResponse<FantasyTeam[]>> = await this.api.get(`/fantasy/${contestId}/teams`, { params });
      if (response.data.success) return this.normalizeIds(response.data.data);
      throw new Error(response.data.message || 'Failed to fetch teams');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch teams');
      throw error;
    }
  }

  async checkTeamNameAvailability(name: string, contestId: string): Promise<boolean> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/fantasy/team/name-availability', { params: { name, contestId } });
      return response.data.data?.available ?? response.data.success;
    } catch { return false; }
  }

  async saveTeamName(contestId: string, fantasyName: string): Promise<{ fantasyName: string }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/fantasy/${contestId}/team-name`, { fantasyName });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Failed to save team name');
  }

  async getTeamName(contestId: string): Promise<string | null> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/fantasy/${contestId}/team-name`);
      return response.data.data?.fantasyName || null;
    } catch { return null; }
  }

  async getTeamLineup(contestId: string, teamId: string, matchId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/fantasy/${contestId}/team/${teamId}/match/${matchId}/lineup`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch lineup');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch lineup');
      throw error;
    }
  }

  async getTeamLineupsBreakdown(contestId: string, teamId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/fantasy/${contestId}/team/${teamId}/lineups`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch lineups');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch lineups');
      throw error;
    }
  }

  /**
   * Per-match team history with lineup snapshots, transfer events, and
   * per-player points. Backs the Timeline tab on the team-breakdown modal:
   * one section per lineup-range (so multi-window contests visibly tell
   * the "who played which match range" story).
   */
  async getTeamHistory(contestId: string, teamId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/fantasy/${contestId}/team/${teamId}/history`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch team history');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch team history');
      throw error;
    }
  }

  async executeTransfer(contestId: string, teamId: string, data: { playersIn?: number[]; playersOut?: number[]; captainPlayerId?: number | null; viceCaptainPlayerId?: number | null }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/fantasy/${contestId}/team/${teamId}/transfer`, data);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to execute transfer');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to execute transfer');
      throw error;
    }
  }

  async getTransferStatus(contestId: string, teamId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/fantasy/${contestId}/team/${teamId}/transfer-status`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch transfer status');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch transfer status');
      throw error;
    }
  }

  // Player methods — normalizes squad API response to Player interface
  async getPlayers(seriesId: string): Promise<Player[]> {
    try {
      // Use GET endpoint — works without body validation issues
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/series/${seriesId}/squads`);

      if (response.data.success) {
        // Normalize squad API fields to Player interface
        return (response.data.data || []).map((p: any) => ({
          id: String(p.playerId ?? p.id ?? p._id),
          name: p.playerName || p.name || '',
          role: this.normalizeRole(p.playerRole || p.role || ''),
          teamId: String(p.teamId ?? ''),
          teamName: p.teamInfo?.teamName || p.teamName || '',
          avatar: p.playerId ? `https://cricclash-s3.s3.us-east-2.amazonaws.com/players/${p.playerId}.jpg` : undefined,
          credits: p.credits ?? 0,
          points: p.points ?? 0,
          battingStyle: p.battingStyle,
          bowlingStyle: p.bowlingStyle,
          playerId: p.playerId,
          squadId: p.squadId,
          intlTeam: p.intlTeam,
          isOverseas: p.isOverseas || false,
        }));
      } else {
        throw new Error(response.data.message || 'Failed to fetch players');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch players');
      }
      throw error;
    }
  }

  private normalizeRole(role: string): 'batsman' | 'bowler' | 'all-rounder' | 'wicket-keeper' {
    const r = (role || '').toLowerCase().trim();
    if (r.includes('bat')) return 'batsman';
    if (r.includes('bowl')) return 'bowler';
    if (r.includes('all') || r.includes('ar')) return 'all-rounder';
    if (r.includes('wk') || r.includes('keep') || r.includes('wicket')) return 'wicket-keeper';
    return 'batsman'; // fallback
  }

  // Contest methods
  async createContest(contestData: any): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
        '/contest',
        contestData
      );

      if (response.data.success) {
        // API returns { contestId: "..." } — normalize to include id
        const data = response.data.data;
        return { ...data, id: data.contestId || data.id || data._id };
      } else {
        throw new Error(response.data.message || 'Failed to create contest');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to create contest');
      }
      throw error;
    }
  }

  async getContestConfig(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/contest/config/rules');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest config');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest config');
      }
      throw error;
    }
  }

  // Fetch all contests user has access to (both owned and joined)
  async getUserContests(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/contest');

      if (response.data.success) {
        return this.normalizeIds(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch user contests');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch user contests');
      }
      throw error;
    }
  }

  // Google authentication
  async googleAuth(token: string, tokenType: 'id_token' | 'access_token' = 'id_token'): Promise<any> {
    try {
      const body = tokenType === 'access_token' ? { accessToken: token } : { idToken: token };
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/google/auth', body);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Google authentication failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Google authentication failed');
      }
      throw error;
    }
  }

  // Google complete-profile
  async completeGoogleProfile(data: { firstname: string; lastname: string; idToken: string }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/google/complete-profile', data);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to complete profile');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to complete profile');
      }
      throw error;
    }
  }

  // Fetch a single contest by ID
  async getContestById(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/contest/${contestId}`);

      if (response.data.success) {
        // API returns array from aggregate pipeline — extract first element
        const data = response.data.data;
        const contest = Array.isArray(data) ? data[0] : data;
        if (!contest) throw new Error('Contest not found');
        return this.normalizeId(contest);
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest');
      }
      throw error;
    }
  }

  // Fetch members of a contest
  async getContestMembers(contestId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/contest-member/${contestId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest members');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest members');
      }
      throw error;
    }
  }

  // Fetch squads for a series
  async getSeriesSquads(seriesId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/series/${seriesId}/squads`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch series squads');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch series squads');
      }
      throw error;
    }
  }


  // Fetch credit balance
  async getCreditBalance(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/credits/balance');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch credit balance');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch credit balance');
      }
      throw error;
    }
  }

  // Fetch credit transactions
  async getCreditTransactions(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/credits/transactions');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch credit transactions');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch credit transactions');
      }
      throw error;
    }
  }

  // Stripe payment methods
  async getStripePackages(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/stripe/packages');
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch packages');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch packages');
      throw error;
    }
  }

  // In-app notifications
  async getNotifications(page = 1, limit = 30): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/in-app-notifications', { params: { page, limit } });
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch notifications');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch notifications');
      throw error;
    }
  }

  async getUnreadNotificationCount(): Promise<{ unreadCount: number }> {
    try {
      const response: AxiosResponse<ApiResponse<{ unreadCount: number }>> = await this.api.get('/in-app-notifications/unread-count');
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch unread count');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch unread count');
      throw error;
    }
  }

  async markNotificationAsRead(id: string): Promise<void> {
    try {
      await this.api.put(`/in-app-notifications/${id}/read`);
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to mark as read');
      throw error;
    }
  }

  async markAllNotificationsAsRead(): Promise<void> {
    try {
      await this.api.put('/in-app-notifications/read-all');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to mark all as read');
      throw error;
    }
  }

  async createCheckoutSession(packageId: string, successUrl: string, cancelUrl: string): Promise<{ sessionId: string; url: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ sessionId: string; url: string }>> = await this.api.post('/stripe/create-checkout-session', {
        packageId,
        successUrl,
        cancelUrl,
      });
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to create checkout session');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to create checkout session');
      throw error;
    }
  }

  // Contest lifecycle
  async publishContest(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/contest/${contestId}/publish`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to publish contest');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to publish contest');
      throw error;
    }
  }

  async unpublishContest(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/contest/${contestId}/unpublish`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to unpublish contest');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to unpublish contest');
      throw error;
    }
  }

  async regenerateContestCode(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/contest/${contestId}/regenerate-code`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to regenerate code');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to regenerate code');
      throw error;
    }
  }

  async getMatchScorecard(seriesId: string, matchId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/series/${seriesId}/match/${matchId}`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch scorecard');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch scorecard');
      throw error;
    }
  }

  async upgradeContestPlan(contestId: string, data: any): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/contest/${contestId}/upgrade`, data);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to upgrade plan');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to upgrade plan');
      throw error;
    }
  }

  async checkContestNameAvailability(name: string, seriesId: string): Promise<boolean> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/contest/name-availability', { params: { seriesId, name } });
      return response.data.success;
    } catch {
      return false;
    }
  }

  async getContestLockStatus(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/contest/${contestId}/lock-status`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch lock status');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch lock status');
      throw error;
    }
  }

  async getContestFantasyPoints(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/contest/${contestId}/fantasy-points`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch fantasy points');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch fantasy points');
      throw error;
    }
  }

  async getPlayerScores(seriesId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/series/${seriesId}/playerScores`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch player scores');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch player scores');
      throw error;
    }
  }

  async deleteContest(contestId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/contest/${contestId}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete contest');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to delete contest');
      throw error;
    }
  }

  async updateContest(contestId: string, data: any): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/contest/${contestId}`, data);
      if (response.data.success) return this.normalizeId(response.data.data);
      throw new Error(response.data.message || 'Failed to update contest');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to update contest');
      throw error;
    }
  }

  // Contest member operations
  async updateContestMemberStatus(contestId: string, memberId: string, data: any): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/contest-member/${contestId}/${memberId}`, data);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to update member status');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to update member status');
      throw error;
    }
  }

  async removeContestMember(contestId: string, memberId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/contest-member/${contestId}/${memberId}`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to remove member');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to remove member');
      throw error;
    }
  }

  // Generic GET method for flexibility
  async get(endpoint: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return await this.api.get(endpoint);
  }

  // Generic POST method for flexibility
  async post(endpoint: string, data?: any): Promise<AxiosResponse<ApiResponse<any>>> {
    return await this.api.post(endpoint, data);
  }

  // Generic PUT method for flexibility
  async put(endpoint: string, data?: any): Promise<AxiosResponse<ApiResponse<any>>> {
    return await this.api.put(endpoint, data);
  }

  // Generic PATCH method for flexibility
  async patch(endpoint: string, data?: any): Promise<AxiosResponse<ApiResponse<any>>> {
    return await this.api.patch(endpoint, data);
  }

  // Generic DELETE method for flexibility
  async delete(endpoint: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return await this.api.delete(endpoint);
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!tokenStorage.getAccessToken();
  }

  getCurrentUser(): User | null {
    return tokenStorage.getUser();
  }

  // ── Badges ──

  async getContestBadges(contestId: string, matchId?: number): Promise<any[]> {
    try {
      const params = matchId ? { matchId } : {};
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/contest/${contestId}/badges`, { params });
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch badges');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch badges');
      throw error;
    }
  }

  async getBadgeSummary(contestId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/contest/${contestId}/badges/summary`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch badge summary');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch badge summary');
      throw error;
    }
  }

  async getBadgeLeaders(contestId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/contest/${contestId}/badges/leaders`);
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch badge leaders');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch badge leaders');
      throw error;
    }
  }

  async getBadgeDefinitions(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/contest/definitions');
      if (response.data.success) return response.data.data;
      throw new Error(response.data.message || 'Failed to fetch badge definitions');
    } catch (error) {
      if (error instanceof AxiosError) throw new Error(error.response?.data?.message || 'Failed to fetch badge definitions');
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get('/health');
      return response.status === 200;
    } catch {
      return false;
    }
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

// Export types for use in components
export type {
  LoginRequest,
  LoginResponse,
  LoginApiResponse,
  User,
  Series,
  Match,
  Team,
  Player,
  Contest,
  FantasyTeam,
  ApiResponse
};
